import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import {
  Check,
  ArrowLeft,
  Link,
  ArrowRight,
  Car,
  RefreshCw,
} from "lucide-react";
import {
  initializeDimoSDK,
  DimoAuthProvider,
  LoginWithDimo,
  useDimoAuthState,
} from "@dimo-network/login-with-dimo";
import { useAuth } from "../hooks/useAuth";
import API_CONFIG from "../lib/ApiConfig";
import syncIcon from "../assets/images/sync-dimo.svg";
import {
  useInteractionTracking,
  useErrorTracking,
} from "../hooks/useAnalytics";

// Initialize the SDK once
try {
  console.log("Initializing DIMO SDK");
  initializeDimoSDK({
    clientId: "0xC2971e0f3d4184c744893d522A8aa20491f239a6",
    apiKey: "608d8656161c2c7bd15db20d9594b9c8b69bc39cf5a135428a7c051517220455",
    redirectUri:
      process.env.REACT_APP_DIMO_REDIRECT_URL ||
      "https://dimo.ownli.app/onboarding/dimo-callback",
    options: {
      forceEmail: true,
    },
  });
  console.log("DIMO SDK initialized successfully");
} catch (err) {
  console.error("DIMO SDK initialization error:", err);
}

// Top-level wrapper with DimoAuthProvider
const DimoConnectScreen = (props) => {
  return (
    <DimoAuthProvider>
      <DimoConnectScreenInner {...props} />
    </DimoAuthProvider>
  );
};

// Actual component with business logic
const DimoConnectScreenInner = ({ userData, onComplete, onBack, onSkip }) => {
  const { getIdToken, user, loading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [integration, setIntegration] = useState(null);
  const [vehicles, setVehicles] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const {
    isAuthenticated,
    getValidJWT,
    getEmail,
    email,
    walletAddress,
  } = useDimoAuthState();
  const { trackButtonClick } = useInteractionTracking();

  useEffect(() => {
    console.log("useEffect: Dimo");
    if (!authLoading && user) {
      const checkIntegration = async () => {
        try {
          setIsLoading(true);
          const token = await getIdToken();
          if (!token) throw new Error("Failed to get authentication token");

          const url = API_CONFIG.getUrl("api/users/dimo/integration");
          const response = await fetch(url, {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
              "x-user-token": token,
            },
          });

          if (!response.ok) {
            if (response.status === 404) {
              console.log("No DIMO integration found for user");
              setIntegration(null);
              setVehicles([]);
            } else {
              throw new Error(
                `Failed to check DIMO integration: ${response.status}`
              );
            }
          } else {
            const integrationData = await response.json();
            console.log("DIMO integration found:", integrationData);
            setIntegration(integrationData);
            setVehicles(integrationData.vehicles || []);
          }
        } catch (error) {
          console.error("Error checking DIMO integration:", error);
          setError(error.message);
        } finally {
          setIsLoading(false);
        }
      };
      checkIntegration();
    } else if (!authLoading && !user) {
      // Auth finished loading but no user - handle accordingly
      setIsLoading(false);
    }
  }, [authLoading, user, getIdToken]);

  const handleRefreshVehicles = async () => {
    try {
      setRefreshing(true);
      setError(null);
      const token = await getIdToken();
      if (!token) throw new Error("Failed to get authentication token");

      const url = API_CONFIG.getUrl("api/users/dimo/integration");
      const response = await fetch(url, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          "x-user-token": token,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to refresh integration: ${response.status}`);
      }

      const integrationData = await response.json();
      console.log("Refreshed DIMO integration:", integrationData);
      setIntegration(integrationData);
      setVehicles(integrationData.vehicles || []);
    } catch (error) {
      console.error("Error refreshing integration:", error);
      setError(`Failed to refresh vehicles: ${error.message}`);
    } finally {
      setRefreshing(false);
    }
  };

  const handleLoginError = (error) => {
    console.error("Login error:", error);
    setError(
      `Failed to connect with DIMO: ${error.message || "Unknown error"}`
    );
    setIsLoading(false);
  };

  const handleLoginSuccess = async (result) => {
    try {
      console.log("Login success:", result);
      setError(null);
      setIsLoading(true);
      console.log(walletAddress);
      const walletId = walletAddress;
      if (!walletId) throw new Error("No wallet address was provided by DIMO");

      const token = await getIdToken();
      if (!token) throw new Error("Failed to get authentication token");

      const url = API_CONFIG.getUrl("api/users/dimo/integration");
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "x-user-token": token,
        },
        body: JSON.stringify({
          userId: "unknown",
          walletId: walletId,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to create DIMO integration: ${response.status}`
        );
      }

      const integrationData = await response.json();
      console.log("DIMO integration created:", integrationData);
      setIntegration(integrationData);
      setVehicles(integrationData.vehicles || []);

      // setTimeout(() => {
      //   onComplete({
      //     integration: integrationData,
      //     vehicles: integrationData.vehicles || [],
      //     connectedAt: new Date().toISOString(),
      //   });
      // }, 2000);
    } catch (error) {
      console.error("Error handling DIMO login:", error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinue = () => {
    // Create a data object with information about the integration
    const dimoData = {
      integration: integration,
      vehicles: vehicles,
      connectedAt: new Date().toISOString(),
      skipped: integration ? false : true, // Track if user skipped
    };
    sessionStorage.setItem("dimoData", JSON.stringify(dimoData));

    // Save to session storage
    sessionStorage.setItem("dimoData", JSON.stringify(dimoData));
    onComplete({
      integration: integration,
      vehicles: vehicles,
      connectedAt: new Date().toISOString(),
    });
  };

  // And in the handleSkip function:
  const handleSkip = () => {
    // Create a data object indicating skip
    const dimoData = {
      skipped: true,
      vehicles: [],
      connectedAt: new Date().toISOString(),
    };

    // Save to session storage
    sessionStorage.setItem("dimoData", JSON.stringify(dimoData));

    // Then call onSkip
    onSkip();
  };

  const renderVehicles = () => {
    if (vehicles.length === 0) {
      return (
        <div className="text-center p-6 bg-gray-50 rounded-lg">
          <Car className="w-10 h-10 text-gray-400 mx-auto mb-2" />
          <p className="text-gray-600">
            No vehicles found in your DIMO account
          </p>
        </div>
      );
    }

    return (
      // <div className="space-y-4">
      //   {vehicles.map((vehicle, index) => (
      //     <div
      //       key={vehicle.id || index}
      //       className="p-4 border rounded-lg flex items-center gap-3"
      //     >
      //       <Car className="w-6 h-6 text-blue-500" />
      //       <div className="flex-1">
      //         <p className="font-medium">
      //           {vehicle.year} {vehicle.make} {vehicle.model}
      //         </p>
      //       </div>
      //     </div>
      //   ))}
      // </div>
      <div className="space-y-4">
        {vehicles.map((vehicle, index) => (
          <div key={vehicle.id || index}>
            <div className="grid grid-cols-2 gap-y-2 text-sm">
              <div className="text-darkGrey">Model</div>
              <div className="text-right text-darkGrey">
                {vehicle.make} {vehicle.model}
              </div>
              <div className="text-darkGrey">Mileage</div>
              <div className="text-right text-darkGrey">
                {vehicle.mileage
                  ? vehicle.mileage.toLocaleString() + " miles"
                  : "—"}
              </div>
              <div className="text-darkGrey">VIN</div>
              <div className="text-right text-darkGrey break-all">
                {vehicle.vin || "—"}
              </div>
              <div className="text-darkGrey">Status</div>
              <div className="text-right text-darkGrey">
                {vehicle.status || "Active"}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <CardContent className="pt-6 flex flex-col items-center justify-center min-h-40">
            <div className="w-10 h-10 border-4 border-t-transparent border-blue-600 rounded-full animate-spin mb-4"></div>
            <p className="text-gray-600">Checking DIMO connection...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* <Card className="bg-blue-50 border-blue-100">
        <CardContent className="pt-6">
          <div className="flex items-start gap-3">
            <Car className="w-5 h-5 text-blue-500 mt-1" />
            <div>
              <p className="font-medium text-blue-900">Vehicle Information</p>
              <p className="text-sm text-blue-700">
                Connect with DIMO to sync your vehicle data
              </p>
            </div>
          </div>
        </CardContent>
      </Card> */}

      {integration ? (
        <Card className="border-none shadow-none">
          <CardHeader className="p-4">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto">
              <Check className="w-10 h-10 text-green-500" />
            </div>
            <CardTitle className="text-xl uppercase font-bold !mt-6 text-center">
              Your Vehicle Info Has Been Synced
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4 p-4 pt-2">
            {/* <div className="flex items-center gap-2 text-green-600 bg-green-50 p-3 rounded-lg">
              <Check className="w-5 h-5" />
              <span>
                Connected with wallet {integration.walletId.substring(0, 6)}...
                {integration.walletId.slice(-4)}
              </span>
            </div> */}
            <p className="text-center text-darkGrey">
              Here’s what we pulled from your vehicle via DIMO. Please review to
              make sure everything looks correct.
            </p>

            <div className="pt-5">
              <div className="flex justify-between items-center mb-3">
                <h3 className="font-bold">Vehicle Info Summary</h3>
                {/* <button
                  onClick={handleRefreshVehicles}
                  disabled={refreshing}
                  className="text-blue-600 flex items-center gap-1 text-sm"
                >
                  <RefreshCw
                    className={`w-4 h-4 ${refreshing ? "animate-spin" : ""}`}
                  />
                  Refresh
                </button> */}
              </div>
              {renderVehicles()}
            </div>

            {error && (
              <div className="text-red-600 text-sm p-2 bg-red-50 rounded-lg">
                {error}
              </div>
            )}

            <button
              onClick={handleContinue}
              className="w-full flex items-center justify-center gap-2 uppercase font-bold bg-purpleGradient text-white px-4 py-3 rounded-[10px] hover:bg-purpleGradientDark transition-colors !mt-10"
            >
              Looks Good — Continue
            </button>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-none shadow-none">
          <CardHeader className="p-4">
            <img src={syncIcon} alt="sync icon" />
            <CardTitle className="text-center font-bebasNeue text-3xl font-bold !mt-6">
              Sync Your Driving Data from DIMO
            </CardTitle>
          </CardHeader>
          <CardContent className="p-4 pt-0 space-y-4">
            <p className="text-gray-600 text-center">
              DIMO helps you connect your car data securely and privately. Link
              your vehicle to get personalized insurance quotes.
            </p>

            {error && (
              <div className="text-red-600 text-sm p-2 bg-red-50 rounded-lg">
                {error}
              </div>
            )}

            <div className="flex flex-col items-center gap-3">
              {/* <LoginWithDimo
                className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                mode="popup"
                permissionTemplateId="1"
                onSuccess={handleLoginSuccess}
                onError={handleLoginError}
              >
                <Car className="w-5 h-5" />
              </LoginWithDimo> */}

              <LoginWithDimo
                className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                mode="redirect"
                permissionTemplateId="1,2,3,4,5"
                onSuccess={handleLoginSuccess}
                onError={handleLoginError}
              >
                <Car className="w-5 h-5" />
                Login with DIMO (Redirect)
              </LoginWithDimo>

              {/* <LoginWithDimo
                className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                mode="popup"
                permissionTemplateId="1,2,3,4,5"
                onSuccess={handleLoginSuccess}
                onError={handleLoginError}
                entryState="VEHICLE_MANAGER"
              >
                <Car className="w-5 h-5" />
                Login with DIMO (Popup + Vehicle Manager)
              </LoginWithDimo>

              <LoginWithDimo
                className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                mode="redirect"
                permissionTemplateId="1,2,3,4,5"
                onSuccess={handleLoginSuccess}
                onError={handleLoginError}
              >
                <Car className="w-5 h-5" />
                Login with DIMO (Redirect)
              </LoginWithDimo>

              <LoginWithDimo
                className="w-full flex items-center justify-center gap-2 bg-blue-600 text-white px-4 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                mode="redirect"
                permissionTemplateId="1,2,3,4,5"
                onSuccess={handleLoginSuccess}
                onError={handleLoginError}
                entryState="VEHICLE_MANAGER"
              >
                <Car className="w-5 h-5" />
                Login with DIMO (Redirect + Vehicle Manager)
              </LoginWithDimo> */}

              <button
                onClick={() => {
                  trackButtonClick("dimo_skip", {
                    step_name: "dimo_connect",
                    user_id: userData?.id,
                  });
                  handleSkip();
                }}
                className="w-full border-2 border-darkGrey font-bold text-darkGrey px-4 py-3 rounded-xl transition-colors mt-4"
              >
                Skip
              </button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DimoConnectScreen;
